/**
 * Art archive lightbox / carousel.
 *
 * Clicking an art piece opens it full-screen. The viewer pages through EVERY
 * art piece across all archive pages (data comes from #art-lightbox-data, not
 * just the pieces on the current page), shows the current position
 * (e.g. "5 / 100"), and repeats the hover caption beneath the image.
 */
const initArtLightbox = () => {
  const grid = document.querySelector('.wrapper-art');
  const lightbox = document.getElementById('art-lightbox');
  const dataEl = document.getElementById('art-lightbox-data');

  if (!grid || !lightbox || !dataEl) {
    return;
  }

  let slides = [];

  try {
    slides = JSON.parse(dataEl.textContent);
  } catch (error) {
    slides = [];
  }

  if (!slides.length) {
    return;
  }

  const imageEl = lightbox.querySelector('.art-lightbox-image img');
  const infoEl = lightbox.querySelector('.art-lightbox-info');
  const currentEl = lightbox.querySelector('.art-lightbox-current');
  const totalEl = lightbox.querySelector('.art-lightbox-total');
  const prevButton = lightbox.querySelector('.art-lightbox-prev');
  const nextButton = lightbox.querySelector('.art-lightbox-next');
  const closeButton = lightbox.querySelector('.art-lightbox-close');

  const total = slides.length;
  let currentIndex = 0;
  let lastFocused = null;

  totalEl.textContent = total;

  const render = () => {
    const slide = slides[currentIndex];

    imageEl.setAttribute('src', slide.full || '');
    imageEl.setAttribute('alt', slide.alt || '');
    infoEl.innerHTML = slide.info || '';
    currentEl.textContent = currentIndex + 1;
  };

  const open = (index, trigger) => {
    lastFocused = trigger || null;
    currentIndex = index;
    render();
    lightbox.classList.add('is-open');
    lightbox.setAttribute('aria-hidden', 'false');
    document.body.classList.add('art-lightbox-open');
    closeButton.focus();
  };

  const close = () => {
    lightbox.classList.remove('is-open');
    document.body.classList.remove('art-lightbox-open');

    // Move focus out of the lightbox BEFORE hiding it from assistive tech,
    // and return it to the piece that opened the viewer.
    if (lastFocused) {
      lastFocused.focus();
    }

    lightbox.setAttribute('aria-hidden', 'true');
  };

  const go = (delta) => {
    currentIndex = (currentIndex + delta + total) % total;
    render();
  };

  // Map each visible piece to its position in the full data set by post ID, so
  // pagination never misaligns the click target.
  Array.from(grid.querySelectorAll('.art-piece')).forEach((piece) => {
    const id = Number(piece.getAttribute('data-id'));
    const index = slides.findIndex((slide) => slide.id === id);

    if (index === -1) {
      return;
    }

    piece.setAttribute('role', 'button');
    piece.setAttribute('tabindex', '0');

    piece.addEventListener('click', () => open(index, piece));

    piece.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        open(index, piece);
      }
    });
  });

  prevButton.addEventListener('click', () => go(-1));
  nextButton.addEventListener('click', () => go(1));
  closeButton.addEventListener('click', close);

  // Swipe left/right to page through the artwork on touch devices, where the
  // arrows are small and easy to miss.
  const swipeThreshold = 50;
  let touchStartX = 0;
  let touchStartY = 0;
  let isTracking = false;
  let hasSwiped = false;

  lightbox.addEventListener(
    'touchstart',
    (event) => {
      // Ignore pinch and other multi-touch gestures.
      if (event.touches.length !== 1) {
        isTracking = false;
        return;
      }

      touchStartX = event.touches[0].clientX;
      touchStartY = event.touches[0].clientY;
      isTracking = true;
      hasSwiped = false;
    },
    { passive: true }
  );

  lightbox.addEventListener(
    'touchend',
    (event) => {
      if (!isTracking) {
        return;
      }

      isTracking = false;

      const deltaX = event.changedTouches[0].clientX - touchStartX;
      const deltaY = event.changedTouches[0].clientY - touchStartY;

      // Only count a mostly horizontal move, so a vertical drag never pages.
      if (
        Math.abs(deltaX) < swipeThreshold ||
        Math.abs(deltaX) <= Math.abs(deltaY)
      ) {
        return;
      }

      hasSwiped = true;
      go(deltaX < 0 ? 1 : -1);
    },
    { passive: true }
  );

  lightbox.addEventListener(
    'touchcancel',
    () => {
      isTracking = false;
    },
    { passive: true }
  );

  // Close when clicking the backdrop (outside the figure and controls).
  lightbox.addEventListener('click', (event) => {
    // A swipe that ends on the backdrop still fires a click; that must not
    // close the viewer the swipe just paged.
    if (hasSwiped) {
      hasSwiped = false;
      return;
    }

    if (event.target === lightbox) {
      close();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (!lightbox.classList.contains('is-open')) {
      return;
    }

    if (event.key === 'Escape') {
      close();
    } else if (event.key === 'ArrowLeft') {
      go(-1);
    } else if (event.key === 'ArrowRight') {
      go(1);
    }
  });
};

export default initArtLightbox;
